import { ReactNode, Component, PureComponent, CSSProperties, ReactElement, lazy, Suspense } from 'react';
import _ from 'lodash';
import moment from 'moment';
import { ExcelOptions } from './TimeSeriesStructs';
import { getToolbarHeight } from './ChartHelpers';
import * as WidgetsThingsStructs from "../WidgetsThingsStructs";
import type { CommonAxe } from 'chart.js';
import type { ChartBaseProps } from '../../../Chart/ChartJs';
import { handleExcelDownloadClick } from "../../../Chart/ChartJsUtils";
import { ChartPortData, OutputChartData, ChartsJsonSchemeValidator, chartDateRangeValidator, ChartDateRange } from './ChartsJsonScheme';
import { ToolbarIconInfo, ToolbarIconInfo_ButtonRender, ToolbarIconInfo_GetSettingList } from '@Primitive/ToolbarIconButton';
import { ToolbarButton } from '../../../Tables/ReactTable';
import { IWidgetThingsClass } from '../Utils/IWidgetThingsClass';
import { StateStatus, WebThingPortType } from '@API/WebNodeTypes';
import { getRandomSmallKey, isDefined, isFunction, IsNullOrEmpty } from '@Utils/Micro';
import { DebugEx } from '@Utils/DebugEx';
import { WebNode } from '@API/WebNode';
import { IWidgetDescription, IWidgetSettingsProps, WidgetDescriptionToPlegmaThing, IWidgetDataValidateResp } from '@UI/Widgets/WidgetStructs';
import { Plegma } from '@API/Api';
import { WebThing, WebThingPort } from '@API/WebThing';
import { DateFormat, Formatters } from '@Utils/Formats';
import { eIconType, SettingsGroup, SettingsType } from '@Primitive/SettingsListType';
import { LazyLoadingAnimation } from '@Primitive/Lazy';
import RsDateRangePicker from '@Primitive/RsDateRangePicker';
import { SettingsList } from '@Primitive/SettingsList';
import { BoardTheming } from '@UI/Dashboard/BoardTheming';
// ------------------------------------------------------------------------------------------------------
const enum BarChartKeys { onToolbarIconClick = 'onToolbarIconClick' };
// ------------------------------------------------------------------------------------------------------
const ChartJsBar = lazy(() => import("./Exports/Bar"));
const ChartJsHorizontalBar = lazy(() => import("./Exports/HorizontalBar"));
// =======================================================================================================
export interface BarChartState {
    Data: ChartPortData;
    ScaleOpts: BarChartScaleOpts;
    DateRange: ChartDateRange;
}
export interface BarChartScaleOpts {
    xAxes?: CommonAxe[];
    yAxes?: CommonAxe[];
};

export interface BarChartSettings {
    // global settings for all axis
    Stacked: boolean;
    Horizontal: boolean;

    // axis configuration
    XAxisSettings: CommonAxe[];
    YAxisSettings: CommonAxe[];

    // Daterange
    showDateRange: boolean;
    initialDateRangeDays: number;
    timePicker: boolean;
    timePickerIncrement: number;
    timePickerInitialRange: number;

    // Legend
    showLegend: boolean;
    legendPosition: 'left' | 'right' | 'top' | 'bottom';
    legendUsePointStyle: boolean;
    legendFontColor: string;
    legendFontSize: number;

    /**
     * Allows to download an excel spreadsheet (XLSX file) 
     * with the extracted data from the chart.
     */
    excelDownload: boolean;
    excelOptions: ExcelOptions;

    //Toolbar
    customToolbarIcons: ToolbarIconInfo[];
    defaultIconsColor?: string;
    toolbarIconSize?: number;
    toolbarHeight?: number;
}
// ------------------------------------------------------------------------------------------------------
class BarChart extends Component<WidgetsThingsStructs.PartialIWidgetThingsProps<BarChartSettings>, BarChartState> {
    // ---------------------------------------------------------------------------------------------------
    constructor(props: WidgetsThingsStructs.PartialIWidgetThingsProps<BarChartSettings>) {
        super(props);
        this.state = {
            Data: null,
            ScaleOpts: {
                xAxes: [{
                    stacked: props.WidgetData.Stacked,
                    ticks: { beginAtZero: true }
                }],
                yAxes: [{
                    stacked: props.WidgetData.Stacked,
                    ticks: { beginAtZero: true }
                }]
            },
            DateRange: {
                StartDate: null,
                EndDate: null
            }
        };
    }
    // ---------------------------------------------------------------------------------------------------
    componentDidMount() {
        this.UpdateData(this.props);
    }
    // ---------------------------------------------------------------------------------------------------
    componentDidUpdate(prevProps: WidgetsThingsStructs.PartialIWidgetThingsProps<BarChartSettings>) {
        if (!_.isEqual(prevProps, this.props))
            this.UpdateData(this.props);
    }
    // --------------------------------------------------------------------------------------------
    get iconToolbarIconsProps(): CSSProperties {
        return {
            padding: 0,
            margin: '0 4px',
            width: this.props.WidgetData.toolbarHeight,
            height: this.props.WidgetData.toolbarHeight,
            lineHeight: this.props.WidgetData.toolbarHeight + "px",
            fontSize: this.props.WidgetData.toolbarIconSize,
            cursor: "pointer"
        };
    }
    // ---------------------------------------------------------------------------------------------------
    onToolbarIconClick = (iconInfo: ToolbarIconInfo) => this.props.SendPortEvent(BarChartKeys.onToolbarIconClick, iconInfo.id);
    // ---------------------------------------------------------------------------------------------------
    private UpdateData(props: WidgetsThingsStructs.PartialIWidgetThingsProps<BarChartSettings>) {
        const chartState = this.getDataFromPropsAndSettings(props);
        this.setState({
            Data: chartState.Data,
            ScaleOpts: chartState.ScaleOpts,
            DateRange: chartState.DateRange
        });
    }
    // ---------------------------------------------------------------------------------------------------
    private getDataFromPropsAndSettings(props: WidgetsThingsStructs.PartialIWidgetThingsProps<BarChartSettings>): BarChartState {
        let settings = props.WidgetData;
        let chartState: BarChartState = {
            Data: null,
            ScaleOpts: {
                xAxes: [{
                    stacked: settings.Stacked,
                    ticks: { beginAtZero: true }
                }],
                yAxes: [{
                    stacked: settings.Stacked,
                    ticks: { beginAtZero: true }
                }]
            },
            DateRange: {
                StartDate: null,
                EndDate: null
            }
        };

        let hasDataPortState = false;
        if (_.has(props.PortsStates, PortKeys.Data)) {
            let port = props.PortsStates[PortKeys.Data];
            if (port.State != null && port.State != "") {
                hasDataPortState = true;
                let data: ChartPortData = port.State;

                if (port.LastStateStatus == StateStatus.NoData) {
                    // No data received show default value
                    if (_.has(props.WidgetDefaultValues, PortKeys.Data))
                        chartState.Data = _.cloneDeep(props.WidgetDefaultValues[PortKeys.Data]);
                } else if (port.LastStateStatus == StateStatus.Valid) {
                    // just in case use try-catch
                    try {
                        data.datasets.forEach(v => {
                            // If label is string and background color
                            // an array keep the first color of it.
                            const bgColor = v.backgroundColor;
                            if ((_.isString(data.labels) ||
                                (_.isArray(data.labels) && data.labels.length == 1)) &&
                                _.isArray(bgColor) && bgColor.length > 0)
                                v.backgroundColor = bgColor[0];
                        });

                        // Collect axes IDs from configuration.
                        let axesIDs = _.compact(_.concat(
                            _.map(data.xAxes, v => v.id),
                            _.map(data.yAxes, v => v.id),

                            this.useXAxisSettings(settings)
                                ? _.map(settings.XAxisSettings, v => v.id)
                                : [],

                            this.useYAxisSettings(settings)
                                ? _.map(settings.YAxisSettings, v => v.id)
                                : []
                        ));

                        const unlinkAxis = (id: string) => {
                            return isDefined(id) && _.indexOf(axesIDs, id) == -1;
                        };

                        // Remove unlinked axes.
                        _.forEach(data.datasets, v => {
                            if (unlinkAxis(v.xAxisID)) delete v.xAxisID;
                            if (unlinkAxis(v.yAxisID)) delete v.yAxisID;
                        });

                        // Remove any x and y axes configuration.
                        chartState.Data = _.cloneDeep(_.omit(data, ['xAxes', 'yAxes']));
                        chartState.ScaleOpts = this._getScaleOptions(settings);

                        // Set the needed scale options.
                        const updateScaleOpts = (v: CommonAxe) => {
                            if (!isDefined(v.stacked))
                                v.stacked = settings.Stacked;
                            if (!isDefined(v.ticks))
                                v.ticks = { beginAtZero: true };
                            else if (!isDefined(v.ticks.beginAtZero))
                                v.ticks.beginAtZero = true;
                        };

                        // Collect axes IDs from datasets.
                        axesIDs = _.compact(_.concat(
                            _.map(data.datasets, v => v.xAxisID),
                            _.map(data.datasets, v => v.yAxisID)
                        ));

                        // Keep linked axes.
                        data.xAxes = _.filter(
                            data.xAxes,
                            v => _.indexOf(axesIDs, v.id) > -1
                        );
                        data.yAxes = _.filter(
                            data.yAxes,
                            v => _.indexOf(axesIDs, v.id) > -1
                        );

                        // Update scale options.
                        if (data.xAxes.length > 0) {
                            _.forEach(data.xAxes, v => updateScaleOpts(v));
                            chartState.ScaleOpts.xAxes = data.xAxes;
                        }
                        if (data.yAxes.length > 0) {
                            _.forEach(data.yAxes, v => updateScaleOpts(v));
                            chartState.ScaleOpts.yAxes = data.yAxes;
                        }

                    } catch (ex) {
                        DebugEx.TraceError(ex);
                    }
                }
                // else data is not valid; show proper message in render
            }
        }

        // no data from port state?
        if (!hasDataPortState) {
            // try to get default values
            if (_.has(props.WidgetDefaultValues, PortKeys.Data)) {
                chartState.Data = _.cloneDeep(props.WidgetDefaultValues[PortKeys.Data]);
            } else {
                chartState.Data = null;
            }
        }

        let hasDateRangePortState = false;
        if (_.has(props.PortsStates, PortKeys.DateRange)) {
            let dateRangePort = props.PortsStates[PortKeys.DateRange];

            // is it touched?
            if (!IsNullOrEmpty(dateRangePort.State)) {
                hasDateRangePortState = true;

                // set the date range
                switch (dateRangePort.LastStateStatus) {
                    case StateStatus.Valid:
                        chartState.DateRange = _.cloneDeep(dateRangePort.State);
                        break;
                    case StateStatus.NoData:
                        chartState.DateRange = _.cloneDeep(props.WidgetDefaultValues[PortKeys.DateRange]);
                        break;
                    default:
                        break;
                }
            }
        }

        // if no data then try to get the default date range
        if (!hasDateRangePortState) {
            if (_.has(props.WidgetDefaultValues, PortKeys.DateRange)) {
                chartState.DateRange = _.cloneDeep(props.WidgetDefaultValues[PortKeys.DateRange]);
            }
        }

        return chartState;
    }
    // ---------------------------------------------------------------------------------------------------
    private useXAxisSettings(WidgetData: BarChartSettings): boolean {
        return WidgetData.Horizontal && isDefined(WidgetData.XAxisSettings) && WidgetData.XAxisSettings.length > 0;
    }
    private useYAxisSettings(WidgetData: BarChartSettings): boolean {
        return !WidgetData.Horizontal && isDefined(WidgetData.YAxisSettings) && WidgetData.YAxisSettings.length > 0;
    }
    // ---------------------------------------------------------------------------------------------------
    private _getScaleOptions(WidgetData: BarChartSettings): BarChartScaleOpts {
        const stacked = (WidgetData.Stacked) ? true : false;
        return {
            xAxes: !this.useXAxisSettings(WidgetData) ?
                [{ stacked: stacked, ticks: { beginAtZero: true } }] :
                _.map(WidgetData.XAxisSettings, (value, index) => {
                    return _.assign({}, value, { stacked: stacked, ticks: { beginAtZero: true } })
                }),
            yAxes: !this.useYAxisSettings(WidgetData) ?
                [{ stacked: stacked, ticks: { beginAtZero: true } }] :
                _.map(WidgetData.YAxisSettings, (value, index) => {
                    return _.assign({}, value, { stacked: stacked, ticks: { beginAtZero: true } })
                })
        };
    }
    // ---------------------------------------------------------------------------------------------------
    private _sendDaterangePortEvent = (startDay: string, endDay: string) => {
        // Send port event msg for the selected range.
        this.props.SendPortEvent(PortKeys.SelectedRange, JSON.stringify({
            StartDate: startDay,
            EndDate: endDay
        } as ChartDateRange));
    }
    // ---------------------------------------------------------------------------------------------------
    render() {
        const {
            Data,
            DateRange,
            ScaleOpts
        } = this.state;

        const {
            width,
            height,
            NodeKey,
            ThingKey,
            WidgetData,
            SendPortEvent,
            WidgetDefaultValues
        } = this.props;

        //todo show be taken from settings
        let toolbarButtons = ToolbarButton.none;
        const showToolbarIcons = (
            isDefined(WidgetData.customToolbarIcons) &&
            WidgetData.customToolbarIcons.length > 0
        ) || toolbarButtons != ToolbarButton.none;

        const pad = WidgetsThingsStructs.defaultWidgetContainerPadding;
        const hideToolbar = !(WidgetData.showDateRange || WidgetData.excelDownload || showToolbarIcons);
        const toolbarHt = hideToolbar ? 0 : getToolbarHeight(height);

        const props: ChartBaseProps = {
            id: ThingKey,
            width: width - 2 * pad,
            height: height - 2 * pad - toolbarHt,
            options: {
                maintainAspectRatio: false,
                animation: null,
                responsive: true,
                scales: ScaleOpts,
                legend: {
                    display: WidgetData.showLegend,
                    position: WidgetData.legendPosition,
                    labels: {
                        fontSize: WidgetData.legendFontSize,
                        fontColor: WidgetData.legendFontColor,
                        usePointStyle: WidgetData.legendUsePointStyle
                    }
                },
                onClick: (evt, elems) => {
                    // find values for each dataset
                    if (elems.length > 0) {
                        let outputData: OutputChartData[] = [];
                        for (let i = 0; i < elems.length; i++) {
                            let elem = elems[i] as any;
                            if (isDefined(elem._datasetIndex) && isDefined(elem._index)) {
                                outputData.push({
                                    Dataset: Data.datasets[elem._datasetIndex].label,
                                    Label: Data.labels[elem._index],
                                    Value: Data.datasets[elem._datasetIndex].data[elem._index],
                                });
                            }
                        }
                        // send port event
                        SendPortEvent(PortKeys.SelectedData, JSON.stringify(outputData));
                    }
                }
            },
            data: Data,
            redraw: true
        };

        let rangeValue = null;
        if (DateRange != null && DateRange.StartDate != null && DateRange.EndDate != null)
            rangeValue = [new Date(DateRange.StartDate), new Date(DateRange.EndDate)];

        return <div style={{ padding: pad }}>
            {/* Toolbar */}
            {hideToolbar ? null
                : <div style={{
                    padding: 0,
                    height: toolbarHt,
                    width: width,
                    backgroundColor: 'transparent',
                    display: "flex",
                    alignContent: "center",
                    justifyContent: "flex-end",
                    alignItems: "center"
                }}>
                    {WidgetData.showDateRange ?
                        <RsDateRangePicker
                            transparent
                            navEnabled
                            usePredefinedRanges
                            size='sm'
                            cleanable={false}
                            localizationKey={NodeKey}
                            hasTime={WidgetData.timePicker}
                            //timePickerIncrement={WidgetData.timePickerIncrement}
                            initialDaysDiff={WidgetData.initialDateRangeDays}
                            initialHoursDiff={WidgetData.timePickerInitialRange}
                            value={rangeValue}
                            onChange={undefined}
                            onChangeBackwardCompatible={this._sendDaterangePortEvent} />
                        : null}
                    {WidgetData.excelDownload
                        ? <i
                            className={BoardTheming.GetTheme(this.props.NodeKey).widgets.charts.excelIcon}
                            style={{ color: WidgetData.defaultIconsColor, ...this.iconToolbarIconsProps }}
                            onClick={() => handleExcelDownloadClick(
                                WidgetData.excelOptions.fileName,
                                WebNode
                                    .GetInstanceKey(NodeKey)
                                    .StateManager
                                    .GetState(ThingKey),
                                PortKeys.Data,
                                WidgetDefaultValues
                            )} />
                        : null}
                    {showToolbarIcons ?
                        ToolbarIconInfo_ButtonRender(
                            WidgetData.customToolbarIcons,
                            [],
                            [],
                            this.iconToolbarIconsProps,
                            this.onToolbarIconClick,
                            'charts'
                        )
                        : null}
                </div>}
            {/* Content */}
            {!isDefined(props.data)
                ? WidgetsThingsStructs.DefaultMessageOnNoData(props.width, props.height)
                : <Suspense fallback={<LazyLoadingAnimation />}>
                    {WidgetData.Horizontal
                        ? <ChartJsHorizontalBar {...props as any} />
                        : <ChartJsBar {...props as any} />}
                </Suspense>}
        </div>;
    }
}
// =======================================================================================================
// Bar Chart Description interface
// =======================================================================================================

class BarWidgetSettings extends PureComponent<IWidgetSettingsProps<BarChartSettings>> {
    // ---------------------------------------------------------------------------------------------------
    constructor(props: IWidgetSettingsProps<BarChartSettings>) {
        super(props);
    }
    // ------------------------------------------------------------------------------------------------
    private getDefaultScaleOptions(axis: string, getXAxis: boolean): CommonAxe {
        return {
            id: axis,
            // "linear", "logarithmic", "time", "radialLinear"
            type: "linear",
            //'top', 'left', 'bottom' and 'right'
            position: getXAxis ? "bottom" : "left",
        }
    }
    // ------------------------------------------------------------------------------------------------
    render() {
        let settings: SettingsGroup[] = [
            {
                name: "",
                elements: [
                    {
                        type: SettingsType.Boolean,
                        label: 'Stacked',
                        dataPath: "Stacked",
                        tooltip: 'Whether datasets will be stacked to each other or not.'
                    },
                    {
                        type: SettingsType.Boolean,
                        label: 'Horizontal',
                        dataPath: "Horizontal",
                        tooltip: 'Whether datasets will be displayed horizontally or not.'
                    },
                    {
                        type: SettingsType.ItemsMgmtDialog,
                        label: 'X Axis',
                        tooltip: 'If Datasets are displayed horizontal, you can modify the x axis.',
                        dataPath: "XAxisSettings",
                        dialogStaticSize: { w: 0.75, h: 0.5 },
                        isHidden: (data: BarChartSettings) => !data.Horizontal,
                        itemsInfo: [
                            {
                                type: SettingsType.String,
                                label: "Axis ID",
                                dataPath: "id",
                                isUnique: true
                            },
                            {
                                type: SettingsType.DropDown,
                                label: "Type",
                                dataPath: "type",
                                items: [
                                    { label: "Linear", value: "linear" },
                                    { label: "Logarithmic", value: "logarithmic" }
                                ]
                            },
                            {
                                type: SettingsType.DropDown,
                                label: "Position",
                                dataPath: "position",
                                items: [
                                    { label: "Top", value: "top" },
                                    { label: "Bottom", value: "bottom" }
                                ]
                            }
                        ],
                        createNewItem: (data) => {
                            return this.getDefaultScaleOptions("xaxis" + getRandomSmallKey(), true);
                        }
                    },
                    {
                        type: SettingsType.ItemsMgmtDialog,
                        label: 'Y Axis',
                        tooltip: 'If horizontal mode is disabled, you can modify the y axis.',
                        dataPath: "YAxisSettings",
                        dialogStaticSize: { w: 0.75, h: 0.5 },
                        isHidden: (data: BarChartSettings) => data.Horizontal,
                        itemsInfo: [
                            {
                                type: SettingsType.String,
                                label: "Axis ID",
                                dataPath: "id",
                                isUnique: true
                            },
                            {
                                type: SettingsType.DropDown,
                                label: "Type",
                                dataPath: "type",
                                items: [
                                    { label: "Linear", value: "linear" },
                                    { label: "Logarithmic", value: "logarithmic" }
                                ]
                            },
                            {
                                type: SettingsType.DropDown,
                                label: "Position",
                                dataPath: "position",
                                items: [
                                    { label: "Right", value: "right" },
                                    { label: "Left", value: "left" }
                                ]
                            }
                        ],
                        createNewItem: () => this.getDefaultScaleOptions("yaxis" + getRandomSmallKey(), false)
                    }
                ]
            },
            {
                name: "Toolbar",
                elements: [
                    {
                        type: SettingsType.ItemsMgmtDialog,
                        label: "Toolbar Custom Icons",
                        tooltip: "Extra custom icons in toolbar",
                        dataPath: "customToolbarIcons",
                        itemsInfo: ToolbarIconInfo_GetSettingList(),
                        createNewItem: () => {
                            return {
                                icon: "",
                                tooltip: "Icon tooltip",
                                iconType: eIconType.fontAwesome,
                                id: "Icon " + getRandomSmallKey(),
                                iconColor: "#2c2c2c",
                                nestedItems: []
                            } as ToolbarIconInfo;
                        }
                    },
                    {
                        type: SettingsType.Slider,
                        label: "Height",
                        dataPath: "toolbarHeight",
                        minValue: 20,
                        maxValue: 80,
                        stepValue: 1,
                        valuePosfix: "px",
                    },
                    {
                        type: SettingsType.Slider,
                        label: "Icon Size",
                        dataPath: "toolbarIconSize",
                        minValue: 14,
                        maxValue: 24,
                        stepValue: 1,
                        valuePosfix: "px",
                    },
                    {
                        type: SettingsType.ColorPicker,
                        label: 'Default Icons Color',
                        tooltip: 'Set value for default Icons.',
                        dataPath: "defaultIconsColor",
                    },
                ]
            },
            {
                name: "Date Range",
                elements: [
                    {
                        type: SettingsType.Boolean,
                        label: 'Show Date Range',
                        dataPath: "showDateRange",
                        tooltip: 'If enabled, a date range picker will be shown in the toolbar.'
                    },
                    {
                        type: SettingsType.Number,
                        label: "Initial Days Period",
                        dataPath: "initialDateRangeDays",
                        tooltip: 'Specify the initial period of the range in days. Default value is set to 30 days, meaning that when you load the page, the range will span from current day to 30 days before.',
                        minValue: 0,
                        isHidden: (data: BarChartSettings) => !data.showDateRange,
                        oneLine: true,
                    },
                    {
                        type: SettingsType.Boolean,
                        label: "Selection of dates with time",
                        dataPath: "timePicker",
                        tooltip: 'Whether to include time in range selection or not.',
                        isHidden: (data: BarChartSettings) => !data.showDateRange
                    },
                    {
                        type: SettingsType.Number,
                        label: "Time Picker increment (min)",
                        dataPath: "timePickerIncrement",
                        tooltip: 'In case you enable time selection, you can specify the step of the time picker in minutes.',
                        maxValue: 60,
                        minValue: 0,
                        isHidden: (data: BarChartSettings) => !data.showDateRange || !data.timePicker,
                        oneLine: true,
                    },
                    {
                        type: SettingsType.Number,
                        label: "Initial Hour Period",
                        dataPath: "timePickerInitialRange",
                        tooltip: 'Specify the initial period of the range in hours.',
                        minValue: 0,
                        isHidden: (data: BarChartSettings) => !data.showDateRange || !data.timePicker,
                        oneLine: true,
                    }
                ]
            },
            {
                name: "Legend",
                elements: [
                    {
                        type: SettingsType.Boolean,
                        label: 'Show Legend',
                        dataPath: "showLegend",
                        tooltip: 'If enabled, show the legend'
                    },
                    {
                        type: SettingsType.DropDown,
                        label: "Position",
                        tooltip: 'Set the position of the legend (top, bottom, left, right)',
                        dataPath: "legendPosition",
                        items: [
                            { value: 'top', label: 'Top' },
                            { value: 'left', label: 'Left' },
                            { value: 'bottom', label: 'Bottom' },
                            { value: 'right', label: 'Right' }
                        ],
                        isHidden: (data) => !data.showLegend
                    },
                    {
                        type: SettingsType.Boolean,
                        label: "Use Point Style",
                        tooltip: 'Label style will match corresponding point style',
                        dataPath: "legendUsePointStyle",
                        isHidden: (data) => !data.showLegend
                    },
                    {
                        type: SettingsType.ColorPicker,
                        label: "Font Color",
                        tooltip: '	Color of legend text',
                        dataPath: "legendFontColor",
                        isHidden: (data) => !data.showLegend
                    },
                    {
                        type: SettingsType.Number,
                        label: "Font Size",
                        tooltip: 'Font size of legend text (9-25)',
                        dataPath: "legendFontSize",
                        minValue: 9,
                        maxValue: 25,
                        isHidden: (data) => !data.showLegend,
                        oneLine: true
                    }
                ]
            },
            {
                name: "Excel Download",
                elements: [
                    {
                        type: SettingsType.Boolean,
                        label: 'Excel Download',
                        tooltip: 'Allows to download an excel spreadsheet (XLSX file) with the extracted data from the chart',
                        dataPath: 'excelDownload'
                    },
                    {
                        type: SettingsType.String,
                        label: "File Name",
                        dataPath: "excelOptions.fileName",
                        tooltip: 'Define excel file name',
                        oneLine: true,
                        isHidden: (data: BarChartSettings) => !data.excelDownload
                    },
                ]
            },
        ]

        return <SettingsList
            AutoSave={true}
            SettingsMap={settings}
            Data={this.props.WidgetData}
            onSaveData={(data) => {
                if (isFunction(this.props.onWidgetDataUpdate))
                    this.props.onWidgetDataUpdate(data);
            }}
            Toolbar={this.props.SettingListToolbar}
        />;
    }
    // ---------------------------------------------------------------------------------------------------
}

const enum PortKeys {
    Data = "data",
    DateRange = 'daterange',
    SelectedData = "SelectedData",
    SelectedRange = "SelectedRange"
}
export class BarChartWidget extends IWidgetThingsClass<BarChartSettings> {
    // ------------------------------------------------------------------------------------------------
    Render(): ReactNode { return <BarChart />; }
    // ------------------------------------------------------------------------------------------------
    public RenderWidgetSettings(key: string, width: number, height: number): ReactElement<any> {
        const parent = this.Parent;
        return <BarWidgetSettings
            key={"Settings-" + key}
            width={width}
            height={height}
            WidgetData={_.cloneDeep(this.WidgetData)} // copy of the data and is return the copy in the update cb
            WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
            onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
            ThingKey={this.ThingKey}
            NodeKey={this.NodeKey}
            SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
        />;
    }
    // ------------------------------------------------------------------------------------------------
    public initThing() {
        let wData = this.WidgetData;

        // Thing description
        let plegmaThing: Plegma.Thing = WidgetDescriptionToPlegmaThing(this.Description);

        // Ports
        let ports: WebThingPort[] = [];
        ports.push(new WebThingPort(
            {
                Name: "Data",
                PortKey: PortKeys.Data,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Input,
                State: ""
            },
            WebThingPortType.Data,
            (data) => this.ValidateValues(PortKeys.Data, data)
        ));

        ports.push(new WebThingPort(
            {
                Name: 'Date Range',
                PortKey: PortKeys.DateRange,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Input,
                State: ''
            },
            WebThingPortType.Data,
            data => this.ValidateValues(PortKeys.DateRange, data)
        ));

        ports.push(new WebThingPort(
            {
                Name: "Selected Data",
                PortKey: PortKeys.SelectedData,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                State: ""
            },
            WebThingPortType.Trigger
        ));

        ports.push(new WebThingPort({
            Name: 'onToolbarIconClick',
            PortKey: BarChartKeys.onToolbarIconClick,
            Type: Plegma.ePortType.String,
            ioDirection: Plegma.ioPortDirection.Output,
            State: ""
        }, WebThingPortType.Data));

        // Calculate the range
        let startDate = moment().startOf('minute').subtract(wData.initialDateRangeDays, 'days');
        if (wData.timePicker)
            startDate.subtract(wData.timePickerInitialRange, "hours");
        let endDate = moment().startOf('minute');
        let dates = Formatters.datetimeRange(startDate, endDate,
            DateFormat.ISO, wData.timePicker, true).split(' - ');

        ports.push(new WebThingPort(
            {
                Name: "Selected Range",
                PortKey: PortKeys.SelectedRange,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                State: JSON.stringify({ StartDate: dates[0], EndDate: dates[1] })
            },
            WebThingPortType.Data
        ));

        // Create the thing
        let thing = new WebThing(this.WebModule, plegmaThing, ports);
        this.ThingKey = thing.ThingKey;
    }
    // ------------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): BarChartSettings {
        return {
            Horizontal: false,
            Stacked: true,
            XAxisSettings: [],
            YAxisSettings: [],

            showDateRange: false,
            initialDateRangeDays: 30,
            timePicker: false,
            timePickerIncrement: 5,
            timePickerInitialRange: 0,

            showLegend: true,
            legendPosition: 'top',
            legendUsePointStyle: false,
            legendFontColor: '#666',
            legendFontSize: 12,

            excelDownload: false,
            excelOptions: {
                fileName: "Data",
            },
            customToolbarIcons: [],
            toolbarHeight: 25,
            toolbarIconSize: 15
        } as BarChartSettings;
    }
    // ------------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return BarChartWidget.GetDefaultWidgetDataStatic(); }
    // ------------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        let data: BarChartSettings = BarChartWidget.GetDefaultWidgetDataStatic();
        return {
            Title: "Bar Chart",
            WidgetTitle: "",
            Key: "BarChartWidget",
            Type: "BarChartWidget",
            Data: data,
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: WidgetsThingsStructs.IconURIPath + "/svg/bar_chart.svg"
        };
    }
    // ------------------------------------------------------------------------------------------------
    public GetDefaultValues(): { [key: string]: any } {
        return {
            [PortKeys.Data]: null
        };
    }
    // ------------------------------------------------------------------------------------------------
    public ValidateValues(key: string, value: any): IWidgetDataValidateResp {
        if (key == PortKeys.Data) {
            return ChartsJsonSchemeValidator().DataValidateString(value);
        } else if (key == PortKeys.DateRange) {
            return chartDateRangeValidator().DataValidateString(value);
        }
        return { valid: true, errors: '' };
    };
    // ------------------------------------------------------------------------------------------------
    public GetLocalizationDictionary() {
        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            return {
                [WidgetsThingsStructs.WidgetLocalizationKeys.Title]: this.Description.WidgetTitle
            };
        else
            // nothing to expose
            return {};
    }
    // ------------------------------------------------------------------------------------------------
    GetDataPortKeyStatic(): string { return PortKeys.Data; };
    // ------------------------------------------------------------------------------------------------
}
// =======================================================================================================